import React from 'react';

const EDGE_EPSILON = 2;

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

export default function useHorizontalTimelineScroll({
  enabled = true,
  signature = '',
  pageStepRatio = 0.9,
  durationAwayMs = 1800,
  durationReturnMs = 1000,
  pauseHomeMs = 20000,
  pauseAwayMs = 4000
} = {}) {
  const containerRef = React.useRef(null);
  // Reattach effects when async content mounts or the layout replaces its node.
  const [element, setElement] = React.useState(null);
  const attachContainer = React.useCallback((node) => {
    containerRef.current = node;
    setElement(node);
  }, []);
  const homeScrollRef = React.useRef(0);
  const awayDirectionRef = React.useRef(1);
  const timeoutRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const measureFrameRef = React.useRef(null);
  const resetFrameRef = React.useRef(null);
  const isAnimatingRef = React.useRef(false);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const measure = React.useCallback(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    const nextHasOverflow = maxScrollLeft > EDGE_EPSILON;
    setHasOverflow((current) => current === nextHasOverflow ? current : nextHasOverflow);
  }, []);

  const resetToRelevantItem = React.useCallback(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    const focusItem = element.querySelector('[data-timeline-focus="true"]');
    const desiredScrollLeft = focusItem
      ? focusItem.getBoundingClientRect().left - element.getBoundingClientRect().left
        + element.scrollLeft - (element.clientWidth - focusItem.offsetWidth) / 2
      : 0;
    const homeScrollLeft = clamp(desiredScrollLeft, 0, maxScrollLeft);

    element.scrollLeft = homeScrollLeft;
    homeScrollRef.current = homeScrollLeft;
    // In the LTR scroll coordinate system, upcoming RTL events are to the left.
    awayDirectionRef.current = homeScrollLeft > EDGE_EPSILON ? -1 : 1;
    measure();
  }, [measure]);

  React.useLayoutEffect(() => {
    const raf = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    resetFrameRef.current = raf(() => {
      resetFrameRef.current = null;
      resetToRelevantItem();
    });

    return () => {
      if (resetFrameRef.current !== null) {
        (window.cancelAnimationFrame || window.clearTimeout)(resetFrameRef.current);
        resetFrameRef.current = null;
      }
    };
  }, [element, hasOverflow, resetToRelevantItem, signature]);

  React.useEffect(() => {
    if (!element) {
      return undefined;
    }

    const scheduleMeasure = () => {
      if (measureFrameRef.current !== null || isAnimatingRef.current) {
        return;
      }

      const raf = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
      measureFrameRef.current = raf(() => {
        measureFrameRef.current = null;
        measure();
      });
    };

    measure();
    element.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', scheduleMeasure);

    const resizeObserver = typeof window.ResizeObserver !== 'undefined'
      ? new window.ResizeObserver(scheduleMeasure)
      : null;
    resizeObserver?.observe(element);

    const mutationObserver = typeof window.MutationObserver !== 'undefined'
      ? new window.MutationObserver(scheduleMeasure)
      : null;
    mutationObserver?.observe(element, { childList: true, subtree: true, characterData: true });

    return () => {
      element.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (measureFrameRef.current !== null) {
        (window.cancelAnimationFrame || window.clearTimeout)(measureFrameRef.current);
        measureFrameRef.current = null;
      }
    };
  }, [element, measure]);

  React.useEffect(() => {
    const prefersReducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || !hasOverflow || prefersReducedMotion) {
      return undefined;
    }

    let isCancelled = false;
    let movingAway = true;
    const nextPageByDirection = { '-1': 1, '1': 1 };

    const animateTo = (element, target, durationMs, onDone) => {
      const start = element.scrollLeft;
      const delta = target - start;

      if (Math.abs(delta) <= EDGE_EPSILON) {
        onDone();
        return;
      }

      const startedAt = window.performance.now();
      const effectiveDuration = Math.max(80, durationMs);
      const easeInOut = (value) =>
        value < 0.5 ? 2 * value * value : 1 - Math.pow(-2 * value + 2, 2) / 2;

      const tick = (now) => {
        if (isCancelled) {
          return;
        }

        if (document.hidden) {
          isAnimatingRef.current = false;
          timeoutRef.current = window.setTimeout(runStep, pauseHomeMs);
          return;
        }

        const progress = Math.min(1, (now - startedAt) / effectiveDuration);
        element.scrollLeft = start + delta * easeInOut(progress);

        if (progress < 1) {
          animationFrameRef.current = window.requestAnimationFrame(tick);
          return;
        }

        animationFrameRef.current = null;
        isAnimatingRef.current = false;
        measure();
        onDone();
      };

      isAnimatingRef.current = true;
      animationFrameRef.current = window.requestAnimationFrame(tick);
    };

    const runStep = () => {
      if (isCancelled) {
        return;
      }

      const element = containerRef.current;
      if (!element) {
        return;
      }

      const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
      if (maxScrollLeft <= EDGE_EPSILON) {
        measure();
        return;
      }

      if (!movingAway) {
        const homeScrollLeft = clamp(homeScrollRef.current, 0, maxScrollLeft);
        animateTo(element, homeScrollLeft, durationReturnMs, () => {
          if (isCancelled) {
            return;
          }
          const nextDirection = awayDirectionRef.current * -1;
          const canMoveInNextDirection = nextDirection > 0
            ? maxScrollLeft - homeScrollLeft > EDGE_EPSILON
            : homeScrollLeft > EDGE_EPSILON;
          if (canMoveInNextDirection) {
            awayDirectionRef.current = nextDirection;
          }
          movingAway = true;
          timeoutRef.current = window.setTimeout(runStep, pauseHomeMs);
        });
        return;
      }

      const pageStep = Math.max(80, Math.floor(element.clientWidth * pageStepRatio));
      const homeScrollLeft = clamp(homeScrollRef.current, 0, maxScrollLeft);
      const direction = awayDirectionRef.current;
      const movingForward = awayDirectionRef.current > 0;
      const target = clamp(homeScrollLeft + direction * pageStep * nextPageByDirection[direction], 0, maxScrollLeft);
      const reachedAwayEdge = movingForward
        ? target >= maxScrollLeft - EDGE_EPSILON
        : target <= EDGE_EPSILON;

      animateTo(element, target, durationAwayMs, () => {
        if (isCancelled) {
          return;
        }
        // Visit every page over successive excursions, returning home after each.
        nextPageByDirection[direction] = reachedAwayEdge ? 1 : nextPageByDirection[direction] + 1;
        movingAway = false;
        timeoutRef.current = window.setTimeout(runStep, pauseAwayMs);
      });
    };

    timeoutRef.current = window.setTimeout(runStep, pauseHomeMs);

    return () => {
      isCancelled = true;
      isAnimatingRef.current = false;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [
    durationAwayMs,
    durationReturnMs,
    enabled,
    element,
    hasOverflow,
    measure,
    pageStepRatio,
    pauseAwayMs,
    pauseHomeMs,
    signature
  ]);

  return {
    containerRef: attachContainer,
    hasOverflow,
    resetToRelevantItem
  };
}
